import {
  DestroyRef,
  Injectable,
  PLATFORM_ID,
  computed,
  inject,
  signal,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Observable, Subject } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';

import { environment } from 'src/environments/environment';

/**
 * Whether the app can currently reach the API.
 *
 * `navigator.onLine` alone is not enough: it reports whether a network
 * interface exists, not whether anything is reachable. A device on school wifi
 * behind a captive portal, or with the uplink down, reports online and every
 * request still fails. So the browser event is treated as a fast hint and
 * confirmed by an actual request to the backend.
 *
 * Exposed as both a signal (for templates and effects) and an observable (for
 * the RxJS-based services already in this codebase).
 */
@Injectable({ providedIn: 'root' })
export class ConnectivityService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);
  private readonly browser = isPlatformBrowser(this.platformId);

  /** Last confirmed reachability of the API. */
  private readonly onlineSignal = signal<boolean>(true);
  /** True only while a probe is in flight, so the UI can show "checking". */
  private readonly checkingSignal = signal<boolean>(false);

  readonly isOnline = this.onlineSignal.asReadonly();
  readonly isChecking = this.checkingSignal.asReadonly();
  readonly isOffline = computed(() => !this.onlineSignal());

  readonly online$: Observable<boolean> = toObservable(this.onlineSignal);

  /** Fires on every false -> true edge. This is the sync trigger. */
  private readonly reconnected = new Subject<void>();
  readonly reconnected$ = this.reconnected.asObservable();

  /** Guards against overlapping probes when several triggers coincide. */
  private probing = false;
  private timer: ReturnType<typeof setInterval> | null = null;

  /**
   * Background probe interval. Long enough to be invisible on a metered
   * connection; short enough that a teacher regaining signal syncs within about
   * half a minute even if no browser event fired.
   */
  private static readonly PROBE_INTERVAL_MS = 30_000;
  private static readonly PROBE_TIMEOUT_MS = 8_000;

  constructor() {
    if (!this.browser) return;

    this.onlineSignal.set(navigator.onLine);

    const onOnline = () => void this.check();
    const onOffline = () => this.setOnline(false);
    // A tab returning to the foreground is the moment a stale state is most
    // visible to the user, so re-check then rather than waiting for the timer.
    const onVisible = () => {
      if (document.visibilityState === 'visible') void this.check();
    };

    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    document.addEventListener('visibilitychange', onVisible);
    this.timer = setInterval(() => void this.check(), ConnectivityService.PROBE_INTERVAL_MS);

    this.destroyRef.onDestroy(() => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      document.removeEventListener('visibilitychange', onVisible);
      if (this.timer) clearInterval(this.timer);
      this.reconnected.complete();
    });

    void this.check();
  }

  /**
   * Confirm reachability against the API.
   *
   * Uses fetch directly rather than HttpClient to stay outside the app's
   * interceptors - a probe must not be retried, queued, or have auth refresh
   * logic run against it.
   */
  async check(): Promise<boolean> {
    if (!this.browser) return false;

    // The browser saying "offline" is reliable in the negative direction: no
    // interface means nothing is reachable, so skip the request entirely.
    if (!navigator.onLine) {
      this.setOnline(false);
      return false;
    }
    if (this.probing) return this.onlineSignal();

    this.probing = true;
    this.checkingSignal.set(true);
    const controller = new AbortController();
    const timeout = setTimeout(
      () => controller.abort(),
      ConnectivityService.PROBE_TIMEOUT_MS,
    );

    try {
      const response = await fetch(this.probeUrl(), {
        method: 'GET',
        cache: 'no-store',
        signal: controller.signal,
        // The probe must not depend on a valid session: a 401 still proves the
        // server answered, which is all we are asking.
        credentials: 'omit',
      });
      this.setOnline(response.ok || response.status === 401 || response.status === 404);
    } catch {
      this.setOnline(false);
    } finally {
      clearTimeout(timeout);
      this.probing = false;
      this.checkingSignal.set(false);
    }

    return this.onlineSignal();
  }

  private probeUrl(): string {
    const base = (environment.apiUrl ?? '').replace(/\/+$/, '');
    // Cache-buster: a proxy serving a stale 200 would make an offline device
    // look online and send the sync loop into a retry storm.
    return `${base}/api/v1/sync/health?t=${Date.now()}`;
  }

  private setOnline(value: boolean): void {
    const previous = this.onlineSignal();
    if (previous === value) return;
    this.onlineSignal.set(value);
    if (!previous && value) this.reconnected.next();
  }
}
